import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { checkoutAPI } from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderNumber, setOrderNumber] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setStatus('error');
        setError('Invalid session');
        return;
      }

      try {
        const result = await checkoutAPI.getStatus(sessionId);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
          setOrderNumber(result.order_number);
          clearCart();
          localStorage.removeItem('pending_order');
        } else if (result.payment_status === 'unpaid') {
          // Payment might still be processing, poll again
          setTimeout(verifyPayment, 2000);
        } else {
          setStatus('error');
          setError('Payment was not completed');
        }
      } catch (err) {
        setStatus('error');
        setError('Failed to verify payment');
      }
    };

    verifyPayment();
  }, [searchParams, clearCart]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-16">
        <div className="max-w-lg mx-auto text-center">
          {status === 'loading' && (
            <div className="space-y-4">
              <Loader2 className="w-16 h-16 mx-auto animate-spin text-gray-400" />
              <h1 className="text-2xl font-light">Verifying your payment...</h1>
              <p className="text-gray-500">Please wait while we confirm your order.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-6">
              <CheckCircle className="w-20 h-20 mx-auto text-green-500" />
              <h1 className="text-3xl font-light">Thank you for your order!</h1>
              <p className="text-gray-600">
                Your order <span className="font-medium">{orderNumber}</span> has been confirmed.
              </p>
              
              <div className="bg-white p-6 rounded-lg shadow-sm text-left space-y-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="font-medium">What's next?</p>
                    <p className="text-sm text-gray-500">
                      You'll receive an email confirmation shortly with your order details.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  We'll notify you when your artwork ships. Most orders are dispatched within 3-5 business days.
                </p>
              </div>

              <Link
                to="/"
                className="inline-block bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors"
                data-testid="continue-shopping-btn"
              >
                Continue Shopping
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <XCircle className="w-20 h-20 mx-auto text-red-500" />
              <h1 className="text-3xl font-light">Something went wrong</h1>
              <p className="text-gray-600">{error}</p>
              
              <div className="space-x-4">
                <Link
                  to="/checkout"
                  className="inline-block bg-black text-white px-8 py-3 rounded-md hover:bg-gray-800 transition-colors"
                >
                  Try Again
                </Link>
                <Link
                  to="/contact"
                  className="inline-block border border-black px-8 py-3 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
